/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { EMAILS } from "@/lib/constants/master";

interface StructuredDataProps {
  type:
    | "organization"
    | "software"
    | "webpage"
    | "faq"
    | "howto"
    | "webapplication"
    | "breadcrumb";
  data?: Record<string, unknown>;
}

/**
 * Structured Data component for SEO
 * Implements schema.org JSON-LD for better search visibility
 */
export function StructuredData({ type, data }: StructuredDataProps) {
  const getSchema = () => {
    switch (type) {
      case "organization":
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Contractor Co-Pilot",
          url: "https://contractor-co-pilot.com",
          logo: "https://contractor-co-pilot.com/logo.png",
          description:
            "The business platform for contractors. Estimates, proposals, projects, budgets, invoices, and an AI assistant built for the trades.",
          contactPoint: {
            "@type": "ContactPoint",
            email: EMAILS.HELLO,
            contactType: "Customer Support",
          },
          sameAs: [
            "https://x.com/contractor_ai",
            "https://www.linkedin.com/company/contractor-co-pilot",
          ],
          ...data,
        };

      case "software":
        return {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Contractor Co-Pilot",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "Construction Business Management Software",
          operatingSystem: "Web Browser",
          description:
            "The business platform for contractors. Estimates, proposals, projects, budgets, invoices, and an AI assistant built for the trades.",
          offers: {
            "@type": "AggregateOffer",
            lowPrice: "0",
            highPrice: "299",
            priceCurrency: "USD",
            priceSpecification: [
              {
                "@type": "UnitPriceSpecification",
                price: "0",
                priceCurrency: "USD",
                name: "Mobile",
              },
              {
                "@type": "UnitPriceSpecification",
                price: "29",
                priceCurrency: "USD",
                name: "Mobile",
                billingDuration: "P1M",
              },
              {
                "@type": "UnitPriceSpecification",
                price: "79",
                priceCurrency: "USD",
                name: "Starter",
                billingDuration: "P1M",
              },
              {
                "@type": "UnitPriceSpecification",
                price: "149",
                priceCurrency: "USD",
                name: "Professional",
                billingDuration: "P1M",
              },
              {
                "@type": "UnitPriceSpecification",
                price: "299",
                priceCurrency: "USD",
                name: "Business",
                billingDuration: "P1M",
              },
            ],
          },
          featureList: [
            "AI-powered document processing",
            "Automated cost calculation",
            "Professional proposal generation",
            "Excel and CSV import",
            "QuickBooks integration",
            "Multi-user collaboration",
            "Project management",
            "Budget tracking and actuals",
            "Invoice generation",
            "AI business assistant",
            "Client portal",
            "Calendar and scheduling",
            "Email hub with AI analysis",
            "Cost database management",
          ],
          ...data,
        };

      case "webpage":
        return {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: data?.name || "Contractor Co-Pilot",
          description:
            data?.description ||
            "The business platform for contractors. Estimates, proposals, projects, budgets, invoices, and AI.",
          url: data?.url || "https://contractor-co-pilot.com",
          publisher: {
            "@type": "Organization",
            name: "Contractor Co-Pilot",
            logo: {
              "@type": "ImageObject",
              url: "https://contractor-co-pilot.com/logo.png",
            },
          },
          ...data,
        };

      case "faq":
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: data?.questions || [],
        };

      case "howto":
        return {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: data?.name || "",
          description: data?.description || "",
          step: data?.steps || [],
          ...data,
        };

      case "webapplication":
        return {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: data?.name || "Contractor Co-Pilot",
          applicationCategory:
            data?.applicationCategory || "UtilityApplication",
          operatingSystem: "Web Browser",
          description: data?.description || "",
          url: data?.url || "https://contractor-co-pilot.com",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          ...data,
        };

      case "breadcrumb":
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: (
            (data?.items as Array<{ name: string; url: string }>) || []
          ).map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            item: item.url,
          })),
        };

      default:
        return null;
    }
  };

  const schema = getSchema();

  if (!schema) return null;

  // Render as a plain <script> tag so Next.js emits it into the SSR HTML.
  // next/script defers injection to client hydration, which means Googlebot's
  // first-pass (JS-disabled) crawl never sees the structured data — breaking
  // every HowTo/FAQ/WebApplication rich result opportunity on the site.
  return (
    <script
      id={`structured-data-${type}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema),
      }}
    />
  );
}

/**
 * HomePage Structured Data
 *
 * Emits SoftwareApplication schema for the home page. The Organization
 * schema is emitted site-wide from `layout.full.tsx`, so we intentionally
 * do NOT duplicate it here — rendering two Organization blocks on the
 * same page confuses Google's entity resolver.
 */
export function HomePageStructuredData() {
  return <StructuredData type="software" />;
}

/**
 * Helper to format FAQ questions for structured data
 */
export function formatFAQForStructuredData(
  questions: Array<{ question: string; answer: string }>,
) {
  return questions.map((q) => ({
    "@type": "Question",
    name: q.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: q.answer,
    },
  }));
}
